/**
 * Which screen the app is on, encoded in the URL's hash.
 *
 * **This exists because a reload used to lose the screen.** The tab was a plain `useState<Tab>("races")`
 * and the garage's class filter lived inside the garage component, so refreshing while looking at the
 * 1:24 trucks silently returned to Races with no filter. On a phone that is not an edge case: the
 * browser reloads a backgrounded tab whenever it wants the memory back.
 *
 * **The hash rather than the path**, for two reasons that are both about not disturbing what works.
 * `/join/<code>` is a real path this app reads and then deliberately erases, so putting tab state in the
 * path would collide with an invite link. And the hash never reaches CloudFront, so restoring a screen
 * cannot break because of an SPA rewrite rule.
 *
 * The parsing is separated from `window` on purpose: the test suite runs in Node with no DOM, so the
 * part with the decisions in it has to be reachable without one.
 */

export type Tab = "races" | "garage" | "records" | "tv" | "team";

export const TABS: { id: Tab; label: string; glyph: string }[] = [
  { id: "races", label: "Races", glyph: "🏁" },
  { id: "garage", label: "Garage", glyph: "🚚" },
  { id: "records", label: "Records", glyph: "🏆" },
  { id: "tv", label: "TVs", glyph: "📺" },
  { id: "team", label: "Team", glyph: "👪" },
];

export interface Place {
  tab: Tab;
  /** The garage's class filter. Empty means "all". Meaningless on any other tab. */
  garageClass: string;
}

const DEFAULT: Place = { tab: "races", garageClass: "" };

function isTab(value: string): value is Tab {
  return TABS.some((entry) => entry.id === value);
}

/**
 * Reads a place out of a hash, e.g. `#garage/1%3A24%20Monster%20Trucks`.
 *
 * Anything unrecognised falls back to Races rather than throwing or rendering nothing, so a stale
 * bookmark, a hand-edited address bar, or a hash left over from a future version cannot strand the app
 * on a blank screen.
 */
export function parseLocation(hash: string): Place {
  const raw = hash.replace(/^#/, "");
  if (!raw) return DEFAULT;
  const slash = raw.indexOf("/");
  const first = slash === -1 ? raw : raw.slice(0, slash);
  const second = slash === -1 ? "" : raw.slice(slash + 1);
  if (!isTab(first)) return DEFAULT;
  let garageClass = "";
  if (second) {
    try {
      garageClass = decodeURIComponent(second);
    } catch {
      // A mangled percent escape is not worth a crash; the tab is still usable without the filter.
      garageClass = "";
    }
  }
  return { tab: first, garageClass: first === "garage" ? garageClass : "" };
}

/** The hash for a place. The class is only ever written for the garage, since nowhere else has one. */
export function formatLocation(place: Place): string {
  const suffix =
    place.tab === "garage" && place.garageClass ? `/${encodeURIComponent(place.garageClass)}` : "";
  return `#${place.tab}${suffix}`;
}
